import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAdmin } from '../../../base/AdminContext';

interface AdminProtectedRouteProps {
    children: React.ReactNode;
    requiredModule?: string;
    requiredSubModule?: string;
    requiredPermission?: 'view' | 'add' | 'edit' | 'delete';
    fallbackPath?: string;
}

const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = ({
    children,
    requiredModule,
    requiredSubModule,
    requiredPermission = 'view',
    fallbackPath = '/admin/login'
}) => {
    const { isAuthenticated, isLoading, checkPermission, hasModuleAccess } = useAdmin();
    const location = useLocation();

    // Hiển thị loading khi đang kiểm tra authentication
    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    // Nếu chưa đăng nhập, redirect về trang login
    if (!isAuthenticated) {
        return <Navigate to={fallbackPath} state={{ from: location }} replace />;
    }

    // Nếu có yêu cầu module cụ thể, kiểm tra quyền
    if (requiredModule) {
        // Kiểm tra quyền truy cập module
        if (!hasModuleAccess(requiredModule)) {
            return (
                <div className="container-fluid mt-4">
                    <div className="alert alert-danger" role="alert">
                        <h4 className="alert-heading">Không có quyền truy cập!</h4>
                        <p>Bạn không có quyền truy cập vào module <strong>{requiredModule}</strong>.</p>
                        <hr />
                        <p className="mb-0">Vui lòng liên hệ quản trị viên để được cấp quyền.</p>
                    </div>
                </div>
            );
        }

        // Kiểm tra quyền cụ thể trong module
        if (!checkPermission(requiredModule, requiredSubModule || null, requiredPermission)) {
            return (
                <div className="container-fluid mt-4">
                    <div className="alert alert-warning" role="alert">
                        <h4 className="alert-heading">Không có quyền thực hiện!</h4>
                        <p>
                            Bạn không có quyền <strong>{getPermissionText(requiredPermission)}</strong> trong{' '}
                            <strong>{requiredModule}</strong>
                            {requiredSubModule && (
                                <span> - <strong>{requiredSubModule}</strong></span>
                            )}.
                        </p>
                        <hr />
                        <p className="mb-0">Vui lòng liên hệ quản trị viên để được cấp quyền.</p>
                    </div>
                </div>
            );
        }
    }

    return <>{children}</>;
};

// Helper function để convert permission type thành text
const getPermissionText = (permission: string): string => {
    switch (permission) {
        case 'view':
            return 'xem';
        case 'add':
            return 'thêm';
        case 'edit':
            return 'sửa';
        case 'delete':
            return 'xóa';
        default:
            return permission;
    }
};

export default AdminProtectedRoute;
